import { formatCurrency, getCurrentRomeTime } from './formatters';
import { getMonthlyData } from '../features/statistics/statisticsLogic';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

/**
 * Genera un testo riepilogativo della situazione finanziaria
 */
export const generateFinancialSummaryText = ({ expenses = [], incomes = [], wallets = [], debts = [] }) => {
  const now = getCurrentRomeTime();
  const currentMonthKey = format(now, 'yyyy-MM');

  // 1. Calcolo Stato Attuale
  const walletsByCategory = {};
  wallets.forEach(w => {
    const type = w.type || 'Altro';
    walletsByCategory[type] = (walletsByCategory[type] || 0) + (parseFloat(w.balance) || 0);
  });

  const totalAssets = Object.values(walletsByCategory).reduce((sum, v) => sum + v, 0);
  
  const personBalances = {};
  if (Array.isArray(debts)) {
    debts.forEach((debt) => {
      const personName = debt?.personName;
      const amount = parseFloat(debt?.amount) || 0;
      if (!personName) return;
      const delta = debt.type === 'credit' ? amount : -amount;
      personBalances[personName] = (personBalances[personName] || 0) + delta;
    });
  }

  const creditTotal = Object.values(personBalances).filter(v => v > 0.01).reduce((sum, v) => sum + v, 0);
  const debtTotal = Object.values(personBalances).filter(v => v < -0.01).reduce((sum, v) => sum + Math.abs(v), 0);
  const netWorth = totalAssets + creditTotal - debtTotal;

  // 2. Dati Storici (mesi passati)
  const allMonthlyData = getMonthlyData(expenses, incomes);
  // Escludiamo il mese in corso e prendiamo gli ultimi 12 mesi
  const pastMonthsData = allMonthlyData
    .filter(m => m.month < currentMonthKey)
    .sort((a, b) => b.month.localeCompare(a.month))
    .slice(0, 12);

  let text = `RIEPILOGO FINANZIARIO - ${format(now, 'dd/MM/yyyy HH:mm')}\n`;
  text += `--------------------------------------------------\n\n`;

  text += `STATO ATTUALE (Patrimonio)\n`;
  text += `- Patrimonio Netto Totale: ${formatCurrency(netWorth)}\n`;
  text += `\nDettaglio Liquidità e Investimenti:\n`;
  
  Object.entries(walletsByCategory)
    .sort((a, b) => b[1] - a[1]) // Ordina per saldo decrescente
    .forEach(([type, amount]) => {
      text += `- ${type}: ${formatCurrency(amount)}\n`;
    });

  if (creditTotal > 0.01) {
    text += `- Crediti Verso Terzi (Totale): ${formatCurrency(creditTotal)}\n`;
  }
  if (debtTotal > 0.01) {
    text += `- Debiti Verso Terzi (Totale): ${formatCurrency(debtTotal)}\n`;
  }
  text += `\n`;

  if (pastMonthsData.length > 0) {
    text += `STATISTICHE MENSILI (Ultimi ${pastMonthsData.length} mesi conclusi):\n`;
    pastMonthsData.forEach(m => {
      try {
        const [year, month] = (m.month || '').split('-').map(Number);
        if (isNaN(year) || isNaN(month)) throw new Error('Invalid month format');
        
        const date = new Date(year, month - 1, 1);
        const monthName = format(date, 'MMMM yyyy', { locale: it });
        const income = parseFloat(m.Entrate) || 0;
        const expense = parseFloat(m.Spese) || 0;
        const savings = income - expense;
        const savingsRate = income > 0 ? (savings / income) * 100 : 0;
        
        text += `- ${monthName}:\n`;
        text += `  • Entrate: ${formatCurrency(income)}\n`;
        text += `  • Uscite: ${formatCurrency(expense)}\n`;
        text += `  • Risparmio: ${formatCurrency(savings)} (${savingsRate.toFixed(1)}%)\n`;
      } catch (err) {
        console.warn('Skipping month due to error:', m.month, err);
        text += `- ${m.month || 'Mese non valido'}: (Dati non disponibili)\n`;
      }
    });
    text += `\n`;

    // Calcolo medie basato sui mesi passati estratti
    const validMonths = pastMonthsData.length;
    if (validMonths > 0) {
      const totalIncome = pastMonthsData.reduce((sum, m) => sum + (parseFloat(m.Entrate) || 0), 0);
      const totalExpense = pastMonthsData.reduce((sum, m) => sum + (parseFloat(m.Spese) || 0), 0);
      const avgIncome = totalIncome / validMonths;
      const avgExpense = totalExpense / validMonths;
      const avgSavings = avgIncome - avgExpense;
      const avgSavingsRate = avgIncome > 0 ? (avgSavings / avgIncome) * 100 : 0;

      text += `MEDIE MENSILI (Periodo ${validMonths} mesi):\n`;
      text += `- Entrate medie: ${formatCurrency(avgIncome)}\n`;
      text += `- Spese medie: ${formatCurrency(avgExpense)}\n`;
      text += `- Risparmio medio: ${formatCurrency(avgSavings)}\n`;
      text += `- Tasso di risparmio medio: ${avgSavingsRate.toFixed(1)}%\n`;
    }
  } else {
    text += `(Nessun dato storico disponibile per i mesi passati)\n`;
  }

  text += `\n--------------------------------------------------\n`;
  text += `Generato da Expense Tracker\n`;

  return text;
};
